import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import { type Components, Virtuoso } from 'react-virtuoso';
import AppCell from '@/components/common/app-cell';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import SearchInput from '@/components/common/search-input';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { BlacklistMember } from '@/hooks/users/use-blacklist';
import useDeleteBlacklist from '@/hooks/users/use-delete-blacklist';
import useFilteredBlacklist from '@/hooks/users/use-filtered-blacklist';
import type { PromiseLoaderReturnType } from '@/routes';
import { makePrompt } from '@/stores/prompter';

export default function BlacklistRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const { filteredBlacklist, setSearchTerm, searchTerm } = useFilteredBlacklist();
  const { mutateAsync: deleteBlacklist, isPending: isDeleting } = useDeleteBlacklist();
  const isDesktop = useIsDesktop();

  const handleRemoveBlacklist = async (member: BlacklistMember) => {
    await makePrompt({
      key: 'remove-blacklist',
      type: 'dialog',
      title: t('blacklist.revoke-hint'),
      content: t('blacklist.revoke-confirm'),
      promise: () => deleteBlacklist(member.BlackUserID),
    });
  };

  return (
    <PageLayout
      title={t('privacy.blacklist')}
      className='gap-0 h-full'
      headerConfig={{
        navigateBack: !isDesktop,
      }}>
      <LoadingLayout promise={loaderData?.promises}>
        {() => (
          <>
            <div className='my-4 mx-5 flex items-center'>
              <SearchInput
                icon
                keyword={searchTerm}
                setKeyword={setSearchTerm}
                clearKeyword={searchTerm ? () => setSearchTerm('') : undefined}
                className='m-0 flex-1'
              />
            </div>

            <Virtuoso
              className='flex-1'
              data={filteredBlacklist}
              overscan={6}
              components={{
                EmptyPlaceholder: () => <EmptyLayout label='blacklist.empty' />,
                Footer: () => (filteredBlacklist.length > 0 ? <div className='pb-safe-or-2' /> : null),
              }}
              itemContent={(_, item) => (
                <AppCell
                  key={item.ID}
                  label={item.BlackUserNickName}
                  className='py-0 my-4 first:mt-0'
                  prefix={
                    <AvatarWithStatus
                      className='size-12'
                      size={[48, 48]}
                      imageUrl={item.BlackUserHeadImg}
                      label={item.BlackUserNickName}
                    />
                  }
                  suffix={
                    <button
                      type='button'
                      className='btn btn-primary btn-sm'
                      disabled={isDeleting}
                      onClick={() => handleRemoveBlacklist(item)}>
                      {t('blacklist.revoke')}
                    </button>
                  }
                />
              )}
            />
          </>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
